// src/pages/admin/FeaturedVideosAdmin.jsx
import { useEffect, useState } from 'react'
import {
  fetchAllFeaturedVideos,
  createFeaturedVideo,
  updateFeaturedVideo,
  deleteFeaturedVideo,
} from '../../lib/featuredVideos'

import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from '../../firebase'
import { Timestamp } from 'firebase/firestore'

const EMPTY_FORM = {
  title: '',
  creatorHandle: '',
  videoUrl: '',
  price: '',
  currency: 'XOF',
  productName: '',
  productImage: '',
  productHref: '/catalog',
  order: 0,
  isActive: true,
  startsAt: '',
  endsAt: '',
  videoFile: null,
  imageFile: null,
}

function tsToInputValue(ts) {
  if (!ts) return ''
  const date =
    ts instanceof Timestamp ? ts.toDate() : new Date(ts)
  const pad = (n) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default function FeaturedVideosAdmin() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  // 👉 contrôle React du modal
  const [isModalOpen, setIsModalOpen] = useState(false)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const list = await fetchAllFeaturedVideos()
      setRows(list)
    } catch (e) {
      console.error(e)
      setError(e?.message || 'Erreur de chargement.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const openNew = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setIsModalOpen(true)
  }

  const openEdit = (row) => {
    setEditing(row)
    setForm({
      ...EMPTY_FORM,
      ...row,
      startsAt: tsToInputValue(row.startsAt),
      endsAt: tsToInputValue(row.endsAt),
      videoFile: null,
      imageFile: null,
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target

    if (type === 'file') {
      setForm((prev) => ({
        ...prev,
        [name]: files && files[0] ? files[0] : null,
      }))
      return
    }

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
  }

  // Upload des fichiers si besoin
  const uploadIfNeeded = async () => {
    const out = {}

    if (form.videoFile) {
      const ext = form.videoFile.name.split('.').pop()
      const path = `featuredVideos/videos/${Date.now()}.${ext}`
      const storageRef = ref(storage, path)
      await uploadBytes(storageRef, form.videoFile)
      out.videoUrl = await getDownloadURL(storageRef)
    }

    if (form.imageFile) {
      const ext = form.imageFile.name.split('.').pop()
      const path = `featuredVideos/images/${Date.now()}.${ext}`
      const storageRef = ref(storage, path)
      await uploadBytes(storageRef, form.imageFile)
      out.productImage = await getDownloadURL(storageRef)
    }

    return out
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)

    try {
      const uploads = await uploadIfNeeded()

      const payload = {
        ...form,
        ...uploads,
        startsAt: form.startsAt ? new Date(form.startsAt) : null,
        endsAt: form.endsAt ? new Date(form.endsAt) : null,
      }

      // on enlève les File du payload Firestore
      delete payload.videoFile
      delete payload.imageFile

      if (editing) {
        await updateFeaturedVideo(editing.id, payload)
      } else {
        await createFeaturedVideo(payload)
      }

      closeModal()
      await load()
    } catch (e) {
      console.error(e)
      alert("Erreur lors de l'enregistrement.")
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = (row) => {
    setDeletingId(row.id)
    if (window.confirm('Supprimer cette vidéo mise en avant ?')) {
      handleDelete(row.id)
    } else {
      setDeletingId(null)
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteFeaturedVideo(id)
      await load()
    } catch (e) {
      console.error(e)
      alert('Erreur lors de la suppression.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="my-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Admin — Featured in videos</h3>
        <button className="btn btn-primary btn-sm" onClick={openNew}>
          + Nouvelle vidéo
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading ? (
        <div className="alert alert-info">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="alert alert-light border">
          Aucune vidéo mise en avant. Clique sur{' '}
          <strong>Nouvelle vidéo</strong> pour en ajouter.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Ordre</th>
                <th>Vidéo</th>
                <th>Produit</th>
                <th>Prix</th>
                <th>Visibilité</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id}>
                  <td>{idx + 1}</td>
                  <td>{r.order}</td>
                  <td>
                    {r.videoUrl && (
                      <video
                        src={r.videoUrl}
                        width="120"
                        height="70"
                        controls
                      />
                    )}
                  </td>
                  <td>
                    <div className="fw-semibold">
                      {r.productName || r.title || '(sans titre)'}
                    </div>
                    {r.productImage && (
                      <img
                        src={r.productImage}
                        alt={r.productName || ''}
                        height="40"
                        className="mt-1 rounded"
                      />
                    )}
                  </td>
                  <td>
                    {Number(r.price || 0).toLocaleString()} {r.currency || 'XOF'}
                  </td>
                  <td className="small">
                    {tsToInputValue(r.startsAt)?.replace('T', ' ')} <br />
                    → {tsToInputValue(r.endsAt)?.replace('T', ' ')}
                  </td>
                  <td>
                    {r.isActive ? (
                      <span className="badge bg-success">Oui</span>
                    ) : (
                      <span className="badge bg-secondary">Non</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-2"
                      onClick={() => openEdit(r)}
                    >
                      Éditer
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => confirmDelete(r)}
                      disabled={deletingId === r.id}
                    >
                      {deletingId === r.id ? 'Suppression…' : 'Supprimer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL géré par React, structure 100% Bootstrap */}
      {/* MODAL géré par React, structure 100% Bootstrap */}
      {isModalOpen && (
        <>
          {/* Backdrop Bootstrap */}
          <div className="modal-backdrop fade show" />

          {/* Fenêtre du modal */}
          <div
            className="modal show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="modal-dialog modal-lg modal-fullscreen-sm-down"
              role="document"
            >
              {/* ⚠️ important : flex + maxHeight pour que le footer reste visible */}
              <div
                className="modal-content d-flex flex-column"
                style={{ maxHeight: '90vh' }}
              >
                <form
                  onSubmit={handleSubmit}
                  className="d-flex flex-column h-100"
                >
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editing
                        ? 'Modifier la vidéo mise en avant'
                        : 'Nouvelle vidéo mise en avant'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeModal}
                      aria-label="Close"
                    />
                  </div>

                  {/* Le corps devient scrollable */}
                  <div className="modal-body flex-grow-1 overflow-auto">
                    <div className="row g-3">
                      {/* Titre + ordre */}
                      <div className="col-md-8">
                        <label className="form-label">Titre</label>
                        <input
                          type="text"
                          name="title"
                          className="form-control"
                          value={form.title}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Ordre</label>
                        <input
                          type="number"
                          name="order"
                          className="form-control"
                          value={form.order}
                          onChange={handleChange}
                        />
                      </div>

                      {/* Créateur + vidéo */}
                      <div className="col-md-6">
                        <label className="form-label">
                          Handle créateur (ex: @nom)
                        </label>
                        <input
                          type="text"
                          name="creatorHandle"
                          className="form-control"
                          value={form.creatorHandle}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Vidéo (mp4, etc.)</label>
                        <input
                          type="file"
                          name="videoFile"
                          accept="video/*"
                          className="form-control"
                          onChange={handleChange}
                        />
                        {/* {form.videoUrl && (
                          <video
                            src={form.videoUrl}
                            controls
                            className="mt-2 rounded"
                            width="120"
                            height="20"
                          />
                        )} */}
                      </div>

                      {/* Produit + image */}
                      <div className="col-md-6">
                        <label className="form-label">Nom du produit</label>
                        <input
                          type="text"
                          name="productName"
                          className="form-control"
                          value={form.productName}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Image produit</label>
                        <input
                          type="file"
                          name="imageFile"
                          accept="image/*"
                          className="form-control"
                          onChange={handleChange}
                        />
                        {/* {form.productImage && (
                          <img
                            src={form.productImage}
                            alt={form.productName || ''}
                            width="20"
                            height="20"
                            className="mt-2 rounded"
                          />
                        )} */}
                      </div>

                      {/* Prix / devise / lien produit */}
                      <div className="col-md-3">
                        <label className="form-label">Prix</label>
                        <input
                          type="number"
                          step="0.01"
                          name="price"
                          className="form-control"
                          value={form.price}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Devise</label>
                        <input
                          type="text"
                          name="currency"
                          className="form-control"
                          value={form.currency}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Lien vers le produit</label>
                        <input
                          type="text"
                          name="productHref"
                          className="form-control"
                          value={form.productHref}
                          onChange={handleChange}
                        />
                      </div>

                      {/* Visibilité (dates) */}
                      <div className="col-md-6">
                        <label className="form-label">Début de visibilité</label>
                        <input
                          type="datetime-local"
                          name="startsAt"
                          className="form-control"
                          value={form.startsAt}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Fin de visibilité</label>
                        <input
                          type="datetime-local"
                          name="endsAt"
                          className="form-control"
                          value={form.endsAt}
                          onChange={handleChange}
                        />
                      </div>

                      <div className="col-12 d-flex align-items-center">
                        <div className="form-check mt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="fvActiveCheck"
                            name="isActive"
                            checked={form.isActive}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="fvActiveCheck"
                          >
                            Vidéo active (visible sur le site)
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Footer toujours visible */}
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={closeModal}
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving
                        ? 'Enregistrement…'
                        : editing
                          ? 'Enregistrer'
                          : 'Créer'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  )
}
